import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Medal, Globe, TrendingUp, ShieldCheck } from 'lucide-react';

const ALOU_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/wivvs6zo_overseas.jpeg';

const lineReveal = {
  hidden: { y: '110%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const trustItems = [
  { icon: Medal, label: 'NCAA Division I' },
  { icon: Globe, label: 'Professional Overseas' },
  { icon: TrendingUp, label: 'Player Development' },
  { icon: ShieldCheck, label: 'Results Parents Can See' },
];

export const BasketballHero = () => {
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section data-testid="basketball-hero" className="relative bg-black pt-28 pb-12 overflow-hidden">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="grid lg:grid-cols-[3fr_2fr] gap-10 items-center">
          <div>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              data-testid="back-to-home"
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-white text-xs tracking-[0.3em] uppercase mb-6 transition-colors"
            >
              ← The Dillon Blueprint
            </motion.button>

            <p className="text-gray-400 font-bold text-xs sm:text-sm tracking-[0.35em] uppercase mb-5">
              The Basketball Blueprint
            </p>

            <h1 data-testid="basketball-headline" aria-label="Serious Players Train Differently." className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.98] uppercase">
              <span className="block overflow-hidden pb-1">
                <motion.span custom={0} variants={lineReveal} initial="hidden" animate="visible" className="block">
                  Serious Players
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span custom={1} variants={lineReveal} initial="hidden" animate="visible" className="block bg-gradient-to-b from-white via-white to-neutral-400 bg-clip-text text-transparent">
                  Train Differently.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="text-gray-300 text-base md:text-lg mt-6 max-w-xl leading-relaxed"
            >
              Basketball development for committed players ready to take their game to the next level.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.0 }}
              data-testid="hero-credentials"
              className="rounded-xl border border-neutral-800 bg-neutral-950 divide-y divide-neutral-800 mt-8 max-w-md"
            >
              <div className="flex items-center gap-4 p-4">
                <Medal className="w-6 h-6 text-gray-300 shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="text-white font-bold text-sm">Former NCAA Division I / Professional Player</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4">
                <Globe className="w-6 h-6 text-gray-300 shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="text-white font-bold text-sm">Overseas Pro Experience</div>
                  <div className="text-gray-500 text-xs">TIPOS SBL • Slovakia Basketball League</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.15 }}
              className="flex flex-col sm:flex-row gap-4 mt-8"
            >
              <button
                data-testid="hero-view-results"
                onClick={() => scrollTo('player-results')}
                className="btn-shine bg-gray-300 hover:bg-white text-black font-bold px-8 py-4 rounded-lg text-center transition-colors"
              >
                <span className="relative z-10">See Player Results →</span>
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mt-8 lg:mt-0"
          >
            <div className="relative rounded-2xl overflow-hidden border border-neutral-800 aspect-[16/10] sm:aspect-[16/9] lg:aspect-[4/5]">
              <img src={ALOU_IMG} alt="Alou Dillon playing professionally" className="absolute inset-0 w-full h-full object-cover object-top ken-burns" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.3 }}
          data-testid="hero-trust-strip"
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-14 pt-8 border-t border-neutral-900"
        >
          {trustItems.map((t) => (
            <div key={t.label} className="flex items-center gap-3">
              <t.icon className="w-6 h-6 text-gray-400 shrink-0" strokeWidth={1.5} />
              <span className="text-gray-300 font-semibold text-sm">{t.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
